import React, { useState, useEffect } from 'react';
import { payrollClosureService } from '../services/payrollClosureService';
import { useAuth } from '../context/AuthContext';
import './PayrollClosureManagement.css';

const PayrollClosureManagement = () => {
  const { hasModuleAccess, loading: authLoading } = useAuth();
  const hasPayrollClosureAccess = hasModuleAccess('PAYROLL_CLOSURES');

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [reopenReason, setReopenReason] = useState('');

  useEffect(() => {
    // Attendre que l'authentification soit chargée avant de faire les appels API
    if (!authLoading && hasPayrollClosureAccess) {
      loadHistory();
      loadSummary();
    }
  }, [authLoading, hasPayrollClosureAccess, selectedYear, selectedMonth]);

  const loadSummary = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await payrollClosureService.getSummary(selectedYear, selectedMonth);
      if (response && response.data) {
        setSummary(response.data);
      } else {
        setError('Réponse invalide du serveur');
        console.error('Invalid response:', response);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setError('Accès refusé. Vous devez avoir le rôle RH pour accéder à cette fonctionnalité.');
      } else if (status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (status === 500) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erreur serveur lors du chargement du résumé';
        setError(errorMessage);
      } else {
        const errorMessage = err.response?.data?.error || err.message || 'Erreur lors du chargement du résumé';
        setError(errorMessage);
      }
      console.error('Error loading summary:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await payrollClosureService.getHistory(selectedYear);
      setHistory(response.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setError('Accès refusé. Vous devez avoir le rôle RH pour accéder à cette fonctionnalité.');
      } else if (status !== 401) {
        // Ne pas afficher d'erreur pour 401 car l'intercepteur gère déjà la redirection
        console.error('Error loading history:', err);
      }
    }
  };

  const handleClosePeriod = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir clôturer la période ${selectedYear}/${String(selectedMonth).padStart(2, '0')} ?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await payrollClosureService.closePeriod({
        year: selectedYear,
        month: selectedMonth,
        notes: closeNotes,
      });
      setSuccess('Période clôturée avec succès !');
      setShowCloseModal(false);
      setCloseNotes('');
      loadSummary();
      loadHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la clôture';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReopenPeriod = async () => {
    if (!reopenReason.trim()) {
      setError('La raison de la réouverture est obligatoire');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir réouvrir cette période ?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await payrollClosureService.reopenPeriod(selectedClosure.id, reopenReason);
      setSuccess('Période réouverte avec succès !');
      setShowReopenModal(false);
      setSelectedClosure(null);
      setReopenReason('');
      loadSummary();
      loadHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la réouverture';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0,000 TND';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const getMonthName = (month) => {
    const date = new Date(2000, month - 1);
    return date.toLocaleString('fr-FR', { month: 'long' });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      CLOSED: { label: 'Clôturé', class: 'status-closed' },
      REOPENED: { label: 'Réouvert', class: 'status-reopened' },
      PENDING: { label: 'En attente', class: 'status-pending' },
    };

    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return (
      <span className={`status-badge ${config.class}`}>
        {config.label}
      </span>
    );
  };

  // Afficher un message de chargement pendant que l'authentification se charge
  if (authLoading) {
    return (
      <div className="loading">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!hasPayrollClosureAccess) {
    return (
      <div className="access-denied">
        <p>Accès refusé. Vous n'avez pas les permissions nécessaires pour accéder à cette fonctionnalité.</p>
      </div>
    );
  }

  return (
    <div className="payroll-closure-management">
      <h1>Gestion de la Clôture Mensuelle des Salaires</h1>

      {error && (
        <div className="error-banner" onClick={() => setError('')}>
          {error} <span className="close-btn">×</span>
        </div>
      )}

      {success && (
        <div className="success-banner" onClick={() => setSuccess('')}>
          {success} <span className="close-btn">×</span>
        </div>
      )}

      <div className="closure-controls">
        <div className="period-selector">
          <label>Année :</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          <label>Mois :</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
              <option key={month} value={month}>
                {getMonthName(month)}
              </option>
            ))}
          </select>

          <button onClick={loadSummary} className="btn btn-secondary">
            Actualiser
          </button>
        </div>
      </div>

      {loading && summary === null ? (
        <div className="loading">Chargement...</div>
      ) : summary && (
        <div className="summary-section">
          <h2>Résumé de la période {selectedYear}/{String(selectedMonth).padStart(2, '0')}</h2>
          
          <div className="summary-grid">
            <div className="summary-card">
              <h3>Employés</h3>
              <p className="summary-value">{summary.totalEmployees}</p>
              <p className="summary-label">Total employés actifs</p>
            </div>

            <div className="summary-card">
              <h3>Fiches de paie</h3>
              <p className="summary-value">{summary.payrollsCreated}</p>
              <p className="summary-label">Créées</p>
              <div className="status-breakdown">
                <span>Brouillons: {summary.payrollsPending}</span>
                <span>Calculées: {summary.payrollsCalculated}</span>
                <span>Validées: {summary.payrollsValidated}</span>
                <span>Payées: {summary.payrollsPaid}</span>
              </div>
            </div>

            <div className="summary-card">
              <h3>Totaux</h3>
              <p className="summary-value">{formatCurrency(summary.totalGrossSalary)}</p>
              <p className="summary-label">Salaire brut total</p>
              <p className="summary-value-small">{formatCurrency(summary.totalNetSalary)}</p>
              <p className="summary-label">Salaire net total</p>
            </div>
          </div>

          {/* Section des retenues et cotisations */}
          <div className="deductions-section">
            <h3>Détail des retenues et cotisations</h3>
            <div className="deductions-grid">
              <div className="deduction-item">
                <span className="deduction-label">Avance sur salaire :</span>
                <span className="deduction-value">{formatCurrency(summary.totalAdvance || 0)}</span>
              </div>
              <div className="deduction-item">
                <span className="deduction-label">CNSS :</span>
                <span className="deduction-value">{formatCurrency(summary.totalCnss || 0)}</span>
              </div>
              <div className="deduction-item">
                <span className="deduction-label">Frais professionnels :</span>
                <span className="deduction-value">{formatCurrency(summary.totalFraisProfessionnels || 0)}</span>
              </div>
              <div className="deduction-item">
                <span className="deduction-label">IRPP :</span>
                <span className="deduction-value">{formatCurrency(summary.totalIrpp || 0)}</span>
              </div>
              <div className="deduction-item">
                <span className="deduction-label">CSS :</span>
                <span className="deduction-value">{formatCurrency(summary.totalCss || 0)}</span>
              </div>
            </div>
          </div>

          {summary.missingEmployees && summary.missingEmployees.length > 0 && (
            <div className="warning-section">
              <h3>⚠️ Employés sans fiche de paie</h3>
              <ul>
                {summary.missingEmployees.map((emp, index) => (
                  <li key={index}>{emp}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="action-buttons">
            <button
              onClick={() => setShowCloseModal(true)}
              className="btn btn-primary"
              disabled={loading || summary.payrollsPending > 0 || summary.payrollsCalculated > 0}
            >
              Clôturer la période
            </button>
          </div>
        </div>
      )}

      <div className="history-section">
        <h2>Historique des clôtures - {selectedYear}</h2>
        {history.length === 0 ? (
          <p>Aucune clôture enregistrée pour cette année.</p>
        ) : (
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>Période</th>
                  <th>Date de clôture</th>
                  <th>Clôturé par</th>
                  <th>Employés</th>
                  <th>Fiches de paie</th>
                  <th>Total brut</th>
                  <th>Total net</th>
                  <th>CNSS</th>
                  <th>IRPP</th>
                  <th>CSS</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((closure) => (
                  <tr key={closure.id}>
                    <td>{closure.closureYear}/{String(closure.closureMonth).padStart(2, '0')}</td>
                    <td>{new Date(closure.closureDate).toLocaleDateString('fr-FR')}</td>
                    <td>{closure.closedBy}</td>
                    <td>{closure.totalEmployees}</td>
                    <td>{closure.totalPayrolls}</td>
                    <td>{formatCurrency(closure.totalGrossSalary)}</td>
                    <td>{formatCurrency(closure.totalNetSalary)}</td>
                    <td>{formatCurrency(closure.totalCnss || 0)}</td>
                    <td>{formatCurrency(closure.totalIrpp || 0)}</td>
                    <td>{formatCurrency(closure.totalCss || 0)}</td>
                    <td>{getStatusBadge(closure.status)}</td>
                    <td>
                      {closure.status === 'CLOSED' && (
                        <button
                          onClick={() => {
                            setSelectedClosure(closure);
                            setShowReopenModal(true);
                          }}
                          className="btn btn-warning btn-sm"
                        >
                          Réouvrir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de clôture */}
      {showCloseModal && (
        <div className="modal-overlay" onClick={() => setShowCloseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Clôturer la période</h2>
              <button className="close-button" onClick={() => setShowCloseModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Vous êtes sur le point de clôturer la période <strong>{selectedYear}/{String(selectedMonth).padStart(2, '0')}</strong>.</p>
              <p>Cette action verrouillera toutes les fiches de paie de cette période.</p>
              
              <div className="form-group">
                <label>Notes (optionnel) :</label>
                <textarea
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  rows="3"
                  placeholder="Ajouter des notes sur cette clôture..."
                />
              </div>

              <div className="modal-actions">
                <button onClick={handleClosePeriod} className="btn btn-primary" disabled={loading}>
                  {loading ? 'Clôture en cours...' : 'Confirmer la clôture'}
                </button>
                <button onClick={() => setShowCloseModal(false)} className="btn btn-secondary">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de réouverture */}
      {showReopenModal && selectedClosure && (
        <div className="modal-overlay" onClick={() => setShowReopenModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Réouvrir la période</h2>
              <button className="close-button" onClick={() => setShowReopenModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Vous êtes sur le point de réouvrir la période <strong>{selectedClosure.closureYear}/{String(selectedClosure.closureMonth).padStart(2, '0')}</strong>.</p>
              
              <div className="form-group">
                <label>Raison de la réouverture * :</label>
                <textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows="4"
                  placeholder="Expliquez la raison de la réouverture..."
                  required
                />
              </div>

              <div className="modal-actions">
                <button onClick={handleReopenPeriod} className="btn btn-warning" disabled={loading || !reopenReason.trim()}>
                  {loading ? 'Réouverture en cours...' : 'Confirmer la réouverture'}
                </button>
                <button onClick={() => setShowReopenModal(false)} className="btn btn-secondary">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollClosureManagement;

